import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { FieldExtensionComponentProps, createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import type { FieldValidation } from '@rjsf/utils';
import { useEffect, useRef } from 'react';

const HOST = 'github.com';
const REPO_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function parseRepoUrl(url: string): { owner: string; repo: string } {
  const params = new URLSearchParams(url?.split('?')[1] ?? '');
  return { owner: params.get('owner') ?? '', repo: params.get('repo') ?? '' };
}

function buildRepoUrl(owner: string, repo: string): string {
  return `${HOST}?owner=${owner}&repo=${repo}`;
}

export const PrefilledRepoUrlPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const allowedOwners = (uiSchema?.['ui:options']?.allowedOwners as string[] | undefined) ?? ['invisible-tech'];

  const { owner, repo } = parseRepoUrl(formData ?? '');
  const currentOwner = owner || allowedOwners[0];

  const nameFromForm = ((formContext?.formData?.name as string) ?? '')
    .toLowerCase()
    .replace(/ /g, '-');

  // Track whether the user has manually edited the repo field
  const userHasModifiedRepo = useRef(false);

  // Display: use formData's repo, or fall back to name-derived value before first sync
  const displayRepo = repo || nameFromForm;

  // Keep repo in sync with the name field unless the user has manually changed it.
  // onChange is intentionally omitted from deps: it changes reference every render but
  // is always fresh when this effect fires (nameFromForm/currentOwner only change on
  // user-triggered re-renders).
  useEffect(() => {
    if (!userHasModifiedRepo.current && nameFromForm) {
      onChange(buildRepoUrl(currentOwner, nameFromForm));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFromForm, currentOwner]);

  const hasError = (rawErrors?.length ?? 0) > 0;

  return (
    <>
      <FormControl fullWidth margin="normal" required={required} error={hasError}>
        <InputLabel id="prefilled-repo-owner-label">Owner</InputLabel>
        <Select
          labelId="prefilled-repo-owner-label"
          value={currentOwner}
          onChange={e => onChange(buildRepoUrl(e.target.value as string, displayRepo))}
        >
          {allowedOwners.map(ownerOption => (
            <MenuItem key={ownerOption} value={ownerOption}>{ownerOption}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        margin="normal"
        label="Repository"
        value={displayRepo}
        required={required}
        error={hasError}
        helperText={rawErrors?.[0] ?? 'Set to name by default. Change it if you want to use a different repository name. Lowercase letters, numbers, and dashes only.'}
        onChange={e => {
          userHasModifiedRepo.current = true;
          onChange(buildRepoUrl(currentOwner, e.target.value));
        }}
      />
    </>
  );
};

export const PrefilledRepoUrlPickerValidation = (
  value: string | undefined,
  validation: FieldValidation,
) => {
  if (!value) {
    validation.addError('Repository location is required.');
    return;
  }
  const { owner, repo } = parseRepoUrl(value);
  if (!owner) validation.addError('Owner is required.');
  if (!repo) validation.addError('Repository name is required.');
  else if (!REPO_PATTERN.test(repo)) {
    validation.addError('Repository name must be lowercase letters, numbers, and dashes only.');
  }
};

export const PrefilledRepoUrlPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PrefilledRepoUrlPicker',
    component: PrefilledRepoUrlPicker,
    validation: PrefilledRepoUrlPickerValidation,
  }),
);
